import React, { useState } from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

export interface QualityCheckContent {
  name: string;
  value: number;
  description?: string;
  chat_history_id?: string;
}

interface QualityCheckDisplayProps {
  qualityCheckDialogContent: QualityCheckContent[];
  className?: string;
}

function QualityCheckDisplay({ qualityCheckDialogContent, className = "" }: QualityCheckDisplayProps) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const normalizeScore = (value: number | null | undefined) =>
    typeof value === "number" && Number.isFinite(value) ? value : 0;

  const getScoreColor = (value: number | null | undefined) => {
    value = normalizeScore(value);
    if (value >= 0.8) return "text-green-600";
    if (value >= 0.6) return "text-yellow-600";
    if (value >= 0.4) return "text-orange-600";
    return "text-red-600";
  };

  const getScoreBgColor = (value: number | null | undefined) => {
    value = normalizeScore(value);
    if (value >= 0.8) return "bg-green-50 border-green-200";
    if (value >= 0.6) return "bg-yellow-50 border-yellow-200";
    if (value >= 0.4) return "bg-orange-50 border-orange-200";
    return "bg-red-50 border-red-200";
  };

  const getProgressColor = (value: number | null | undefined) => {
    value = normalizeScore(value);
    if (value >= 0.8) return "bg-green-500";
    if (value >= 0.6) return "bg-yellow-500";
    if (value >= 0.4) return "bg-orange-500";
    return "bg-red-500";
  };

  const formatScore = (value: number | null | undefined) => {
    value = normalizeScore(value);
    return Math.round(value * 100);
  };

  const getOverallScore = () => {
    if (qualityCheckDialogContent.length === 0) return 0;
    const total = qualityCheckDialogContent.reduce((sum, item) => sum + normalizeScore(item.value), 0);
    return total / qualityCheckDialogContent.length;
  };

  const overallScore = getOverallScore();

  if (qualityCheckDialogContent.length === 0) {
    return (
      <div className={`quality-check-empty ${className}`}>
        <div className="quality-check-empty-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <p className="quality-check-empty-text">No quality checks available</p>
      </div>
    );
  }

  return (
    <div className={`quality-check-container ${className}`}>
      {/* Overall Score Header */}
      <div className={`quality-check-overall ${getScoreBgColor(overallScore)}`}>
        <div className="quality-check-overall-content">
          <div className="quality-check-overall-info">
            <h3 className="quality-check-overall-title">Overall Quality Score</h3>
            <div className={`quality-check-overall-score ${getScoreColor(overallScore)}`}>
              {formatScore(overallScore)}%
            </div>
          </div>
          <div className="quality-check-overall-progress">
            <div className="quality-check-progress-track">
              <div
                className={`quality-check-progress-fill ${getProgressColor(overallScore)}`}
                style={{ width: `${overallScore * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Individual Quality Checks */}
      <div className="quality-check-list">
        {qualityCheckDialogContent.map((item, index) => {
          const isExpanded = expandedItems.has(index);
          const hasDescription = item.description && item.description.trim().length > 0;
          const score = normalizeScore(item.value);

          return (
            <div key={index} className={`quality-check-item ${getScoreBgColor(score)}`}>
              <div
                className={`quality-check-item-header ${hasDescription ? "clickable" : ""}`}
                onClick={hasDescription ? () => toggleExpanded(index) : undefined}
              >
                <div className="quality-check-item-info">
                  <div className="quality-check-item-name">{item.name}</div>
                  <div className={`quality-check-item-score ${getScoreColor(score)}`}>{formatScore(score)}%</div>
                </div>

                <div className="quality-check-item-visual">
                  <div className="quality-check-progress-track">
                    <div
                      className={`quality-check-progress-fill ${getProgressColor(item.value)}`}
                      style={{ width: `${score * 100}%` }}
                    />
                  </div>

                  {hasDescription ? (
                    <button
                      className="quality-check-expand-button"
                      aria-label={isExpanded ? "Collapse details" : "Expand details"}
                    >
                      {isExpanded ? (
                        <UpOutlined style={{ fontSize: "0.7rem" }} />
                      ) : (
                        <DownOutlined style={{ fontSize: "0.7rem" }} />
                      )}
                    </button>
                  ) : (
                    <div style={{ width: "24px" }}></div>
                  )}
                </div>
              </div>

              {hasDescription && isExpanded && (
                <div className="quality-check-item-description">
                  <p>{item.description}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default QualityCheckDisplay;
